/**
 * Timezone formatting utilities
 * Converts IANA timezone identifiers to user-friendly display names
 */

// Timezones
const TIMEZONE_INFO: Record<string, { name: string; country: string }> = {
  // US
  "America/New_York": { name: "Eastern Time (ET)", country: "USA" },
  "America/Chicago": { name: "Central Time (CT)", country: "USA" },
  "America/Denver": { name: "Mountain Time (MT)", country: "USA" },
  "America/Los_Angeles": { name: "Pacific Time (PT)", country: "USA" },
  "America/Anchorage": { name: "Alaska Time (AKT)", country: "USA" },
  "Pacific/Honolulu": { name: "Hawaii Time (HST)", country: "USA" },

  // Canada
  "America/Toronto": { name: "Eastern Time (ET)", country: "Canada" },
  "America/Vancouver": { name: "Pacific Time (PT)", country: "Canada" },

  // Europe
  "Europe/London": { name: "UK Time (GMT/BST)", country: "United Kingdom" },
  "Europe/Paris": { name: "Central European (CET)", country: "France" },
  "Europe/Berlin": { name: "Central European (CET)", country: "Germany" },
  "Europe/Rome": { name: "Central European (CET)", country: "Italy" },
  "Europe/Madrid": { name: "Central European (CET)", country: "Spain" },
  "Europe/Amsterdam": { name: "Central European (CET)", country: "Netherlands" },
  "Europe/Brussels": { name: "Central European (CET)", country: "Belgium" },
  "Europe/Vienna": { name: "Central European (CET)", country: "Austria" },
  "Europe/Zurich": { name: "Central European (CET)", country: "Switzerland" },
  "Europe/Stockholm": { name: "Central European (CET)", country: "Sweden" },
  "Europe/Oslo": { name: "Central European (CET)", country: "Norway" },
  "Europe/Copenhagen": { name: "Central European (CET)", country: "Denmark" },
  "Europe/Helsinki": { name: "Eastern European (EET)", country: "Finland" },
  "Europe/Warsaw": { name: "Central European (CET)", country: "Poland" },
  "Europe/Prague": { name: "Central European (CET)", country: "Czech Republic" },
  "Europe/Budapest": { name: "Central European (CET)", country: "Hungary" },
  "Europe/Bucharest": { name: "Eastern European (EET)", country: "Romania" },
  "Europe/Sofia": { name: "Eastern European (EET)", country: "Bulgaria" },
  "Europe/Athens": { name: "Eastern European (EET)", country: "Greece" },
  "Europe/Istanbul": { name: "Turkey Time (TRT)", country: "Turkey" },
  "Europe/Moscow": { name: "Moscow Time (MSK)", country: "Russia" },

  // Asia
  "Asia/Dubai": { name: "Gulf Time (GST)", country: "UAE" },
  "Asia/Karachi": { name: "Pakistan Time (PKT)", country: "Pakistan" },
  "Asia/Kolkata": { name: "India Time (IST)", country: "India" },
  "Asia/Dhaka": { name: "Bangladesh Time (BST)", country: "Bangladesh" },
  "Asia/Bangkok": { name: "Indochina Time (ICT)", country: "Thailand" },
  "Asia/Jakarta": { name: "Indonesia Time (WIB)", country: "Indonesia" },
  "Asia/Manila": { name: "Philippine Time (PHT)", country: "Philippines" },
  "Asia/Shanghai": { name: "China Time (CST)", country: "China" },
  "Asia/Hong_Kong": { name: "Hong Kong Time (HKT)", country: "Hong Kong" },
  "Asia/Taipei": { name: "Taiwan Time (CST)", country: "Taiwan" },
  "Asia/Tokyo": { name: "Japan Time (JST)", country: "Japan" },
  "Asia/Seoul": { name: "Korea Time (KST)", country: "South Korea" },

  // Australia/Pacific
  "Australia/Sydney": { name: "Australian Eastern (AEST)", country: "Australia" },
  "Australia/Melbourne": { name: "Australian Eastern (AEST)", country: "Australia" },
  "Australia/Brisbane": { name: "Australian Eastern (AEST)", country: "Australia" },
  "Australia/Perth": { name: "Australian Western (AWST)", country: "Australia" },
  "Australia/Adelaide": { name: "Australian Central (ACST)", country: "Australia" },
  "Pacific/Auckland": { name: "New Zealand Time (NZST)", country: "New Zealand" },
  "Pacific/Fiji": { name: "Fiji Time (FJT)", country: "Fiji" },

  // Americas
  "America/Sao_Paulo": { name: "Brasilia Time (BRT)", country: "Brazil" },
  "America/Argentina/Buenos_Aires": { name: "Argentina Time (ART)", country: "Argentina" },
  "America/Mexico_City": { name: "Central Time (CT)", country: "Mexico" },

  // Africa
  "Africa/Cairo": { name: "Eastern European (EET)", country: "Egypt" },
  "Africa/Johannesburg": { name: "South Africa Time (SAST)", country: "South Africa" },
  "Africa/Lagos": { name: "West Africa Time (WAT)", country: "Nigeria" },
  "Africa/Nairobi": { name: "East Africa Time (EAT)", country: "Kenya" },
};

/**
 * Format IANA timezone identifier to user-friendly display name with country
 * @param timezone - IANA timezone identifier (e.g., "America/New_York")
 * @returns Formatted timezone with country first (e.g., "USA • Eastern Time (ET)") or the original if formatting fails
 */
export function formatTimezone(timezone: string | null | undefined): string {
  if (!timezone) {
    return "";
  }

  const info = TIMEZONE_INFO[timezone];
  if (info) {
    return `${info.country} • ${info.name}`;
  }

  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    });
    const parts = formatter.formatToParts(new Date());
    const abbr = parts.find((part) => part.type === "timeZoneName")?.value || "";
    const city = timezone.split("/").pop()?.replace(/_/g, " ") || "";
    return `${city} (${abbr})`;
  } catch (error) {
    return timezone.split("/").pop()?.replace(/_/g, " ") || timezone;
  }
}

/**
 * Get timezone abbreviation (e.g., "ET", "PT", "GMT")
 * @param timezone - IANA timezone identifier
 * @returns Timezone abbreviation
 */
export function getTimezoneAbbreviation(timezone: string | null | undefined): string {
  if (!timezone) {
    return "";
  }

  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    });

    const parts = formatter.formatToParts(new Date());
    const abbreviation = parts.find((part) => part.type === "timeZoneName")?.value;

    return abbreviation || "";
  } catch (error) {
    return "";
  }
}

/**
 * Get current time in the specified timezone
 * @param timezone - IANA timezone identifier
 * @returns Formatted time string in that timezone
 */
export function getCurrentTimeInTimezone(timezone: string | null | undefined): string {
  if (!timezone) {
    return "";
  }

  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    return formatter.format(new Date());
  } catch (error) {
    return "";
  }
}
